import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  authGroupsQuery,
  importGroupRules,
  previewGroupRulesCsv,
  proposeGroupRules,
  removeGroupRule,
  saveGroupRule,
  type AuthGroupRule,
  type AuthGroupsPage,
  type ProposedRule,
} from '../../api/settingsAuthentication'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Ce rol primeste fiecare grup - `settings/authentication/groups/<id>`, element
 * cu element fata de `views/version-3/settings/authentication/groups.php`.
 *
 * ORDINEA E REGULA, deci ecranul o arata ca pe o lista cu coloana "Order" la
 * vedere, nu ca pe un set. Cine citeste trebuie sa poata spune, uitandu-se o
 * singura data, ce primeste cineva aflat in doua grupuri deodata: rolul primei
 * reguli care se potriveste, citind de sus.
 *
 * Un furnizor STINS nu se deschide aici, si asta vine din portal:
 * `Auth_providers::forAuthentication` are `->where('status', 1)` in el, iar
 * controllerul vechi dadea `show_404()`. E ciudat, dar e regula de azi -
 * schimbarea ei e o hotarare, nu o mutare.
 */

type RuleForm = {
  ID: number
  group_value: string
  role_id: number
  sort_order: number
  status: boolean
  all_companies: boolean
  company_ids: number[]
  /** Grupurile de tichete (departamentele) date de regula. */
  ticket_group_ids: number[]
}

const EMPTY: RuleForm = {
  ID: 0,
  group_value: '',
  role_id: 0,
  sort_order: 0,
  status: true,
  all_companies: false,
  company_ids: [],
  ticket_group_ids: [],
}

/** Grupurile de tichete ale unei reguli, in cuvinte. */
export function ticketGroupsText(rule: { ticketGroupNames?: string[] }): string {
  const names = rule.ticketGroupNames ?? []
  return names.length === 0 ? '—' : names.join(', ')
}

/** Ce firme da o regula, in cuvinte: toate, niciuna, sau lista. */
export function companiesText(rule: { allCompanies?: boolean; companyNames?: string[] }): string {
  if (rule.allCompanies) return 'All companies'
  const names = rule.companyNames ?? []
  return names.length === 0 ? '—' : names.join(', ')
}

/**
 * Lista cu cautare si bife a portalului (`SelectPicker`, bootstrap-select),
 * cu valorile alese desenate dedesubt ca etichete, fiecare cu x-ul ei. Asa se
 * aleg Role, Companies si Ticket groups in fereastra regulii - in locul
 * listelor native `<select multiple>`, pe care nu le citea nimeni.
 */
function ChosenLabels({
  options,
  value,
  disabled,
  onRemove,
}: {
  options: Array<{ id: number; name: string }>
  value: number[]
  disabled?: boolean
  onRemove(id: number): void
}) {
  const names = new Map(options.map((o) => [o.id, o.name]))
  const chosen = value.filter((id) => names.has(id))
  if (chosen.length === 0) return null
  return (
    <div className="rule-chosen" style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {chosen.map((id) => (
        <span
          key={id}
          className={`label label-${disabled ? 'default' : 'info'}`}
          style={{ fontWeight: 'normal', fontSize: 12, padding: '3px 6px' }}
        >
          {names.get(id)}
          {disabled ? null : (
            <button
              type="button"
              className="close"
              aria-label={`Remove ${names.get(id) ?? ''}`}
              onClick={() => onRemove(id)}
              style={{ float: 'none', fontSize: 14, marginLeft: 6, color: '#fff', opacity: 0.8, lineHeight: 1 }}
            >
              &times;
            </button>
          )}
        </span>
      ))}
    </div>
  )
}

export function RuleModal({
  page,
  providerId,
  form,
  open,
  onClose,
  onSaved,
}: {
  page: AuthGroupsPage
  providerId: number
  form: RuleForm | null
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const [values, setValues] = useState<RuleForm>(form ?? EMPTY)
  const [problem, setProblem] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setValues(form ?? EMPTY)
    setProblem('')
  }, [form])

  const save = async () => {
    setBusy(true)
    setProblem('')
    try {
      const answer = await saveGroupRule(providerId, {
        ID: values.ID,
        group_value: values.group_value,
        role_id: values.role_id,
        sort_order: values.sort_order,
        status: values.status ? 1 : 0,
        all_companies: values.all_companies ? 1 : 0,
        company_ids: values.all_companies ? [] : values.company_ids,
        /* Semnul spune serverului ca lista a fost trimisa, si cand e goala: un
           formular fara nimic bifat nu trimite niciun `ticket_group_ids`. */
        ...(page.ticketGroups !== undefined
          ? { ticket_groups_sent: 1, ticket_group_ids: values.ticket_group_ids }
          : {}),
      })
      if (answer.status !== 'OK') {
        setProblem(answer.message)
        return
      }
      toast(answer.message, 'Saved', 'success')
      onSaved()
      onClose()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The rule could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal id="ruleModal" open={open} onClose={onClose} title="Group rule" labelledBy="ruleModalLabel">
      <form className="form-horizontal" onSubmit={(event) => event.preventDefault()}>
        <div className="form-group">
          <label className="col-sm-4 control-label">Group</label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              value={values.group_value}
              onChange={(e) => setValues((prev) => ({ ...prev, group_value: e.target.value }))}
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              Exactly what the provider sends &mdash; a name, or an identifier. Matched whole, not partially, so a rule
              for <code>IT</code> does not catch <code>IT-Readonly</code>.
            </span>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-4 control-label">Role</label>
          <div className="col-sm-8">
            <SelectPicker
              id="ruleRole"
              className="form-control"
              title="Choose…"
              size={8}
              options={page.roles.map((role) => ({ value: String(role.id), label: role.name }))}
              value={values.role_id > 0 ? String(values.role_id) : ''}
              onChange={(value) => setValues((prev) => ({ ...prev, role_id: Number(value || 0) }))}
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              Roles that can administer users or roles are not listed.
            </span>
            <span className="help-block" data-testid="rule-role-help" style={{ marginBottom: 0 }}>
              Somebody in several groups gets the role of the first matching rule as main role; the roles of the other
              matching rules are added as extra roles when &ldquo;Extra roles from directory groups&rdquo; is on for the
              provider.
              {page.provider.iamExtraRoles
                ? ' It is on for this provider.'
                : ' It is off for this provider, so only the main role is given.'}
            </span>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-4 control-label">Companies</label>
          <div className="col-sm-8">
            <label style={{ fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={values.all_companies}
                onChange={(e) => setValues((prev) => ({ ...prev, all_companies: e.target.checked }))}
              />{' '}
              All companies of this service provider
            </label>
            <SelectPicker
              id="ruleCompanies"
              className="form-control"
              multiple
              actionsBox
              selectedTextFormat="count > 2"
              title={values.all_companies ? 'All companies' : 'No company'}
              size={8}
              disabled={values.all_companies}
              options={(page.companies ?? []).map((company) => ({ value: String(company.id), label: company.name }))}
              value={values.all_companies ? [] : values.company_ids.map(String)}
              onChange={(value) => setValues((prev) => ({ ...prev, company_ids: value.map(Number) }))}
            />
            <ChosenLabels
              options={page.companies ?? []}
              value={values.all_companies ? [] : values.company_ids}
              onRemove={(id) =>
                setValues((prev) => ({ ...prev, company_ids: prev.company_ids.filter((c) => c !== id) }))
              }
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              Used only when &ldquo;Companies from directory groups&rdquo; is on for the provider. Somebody in several
              groups gets the companies of all of them.
            </span>
          </div>
        </div>

        {page.ticketGroups !== undefined ? (
          <div className="form-group">
            <label className="col-sm-4 control-label">Ticket groups</label>
            <div className="col-sm-8">
              <SelectPicker
                id="ruleTicketGroups"
                className="form-control"
                multiple
                actionsBox
                selectedTextFormat="count > 2"
                title="No ticket group"
                size={8}
                options={page.ticketGroups.map((group) => ({ value: String(group.id), label: group.name }))}
                value={values.ticket_group_ids.map(String)}
                onChange={(value) => setValues((prev) => ({ ...prev, ticket_group_ids: value.map(Number) }))}
              />
              <ChosenLabels
                options={page.ticketGroups}
                value={values.ticket_group_ids}
                onRemove={(id) =>
                  setValues((prev) => ({ ...prev, ticket_group_ids: prev.ticket_group_ids.filter((g) => g !== id) }))
                }
              />
              <span className="help-block" style={{ marginBottom: 0 }}>
                Members of the directory group are put in these ticket groups at sign-in, directory sync and SCIM, and
                taken out when they leave it. Ticket groups given by hand stay.
              </span>
            </div>
          </div>
        ) : null}

        <div className="form-group">
          <label className="col-sm-4 control-label">Order</label>
          <div className="col-sm-8">
            <input
              type="number"
              className="form-control"
              min={0}
              value={values.sort_order}
              onChange={(e) => setValues((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))}
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              Lower runs first. This is what decides for somebody in two groups.
            </span>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-4 control-label">Active</label>
          <div className="col-sm-8" style={{ paddingTop: 8 }}>
            <input
              type="checkbox"
              checked={values.status}
              onChange={(e) => setValues((prev) => ({ ...prev, status: e.target.checked }))}
            />
          </div>
        </div>

        {problem !== '' ? <div className="alert alert-danger">{problem}</div> : null}
      </form>

      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-info" disabled={busy} onClick={() => void save()}>
          Save
        </button>
      </div>
    </Modal>
  )
}

export function AuthGroupRulesPage() {
  const params = useParams({ from: '/settings/authentication/groups/$providerId' })
  const providerId = Number(params.providerId)
  const client = useQueryClient()
  const page = useQuery(authGroupsQuery(providerId))
  const [editing, setEditing] = useState<RuleForm | null>(null)
  const [open, setOpen] = useState(false)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <div id="content">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> That provider was not found. It may have been
          removed, or it is not active.
        </div>
        <a href="/app/settings/authentication">&larr; Back to providers</a>
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const data = page.data
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'authentication'] })
  }

  const drop = async (rule: AuthGroupRule) => {
    if (!window.confirm(`Remove the rule for "${rule.group}"?`)) return
    const answer = await removeGroupRule(providerId, rule.id)
    toast(answer.message, answer.status === 'OK' ? 'Removed' : 'Error', answer.status === 'OK' ? 'success' : 'error')
    if (answer.status === 'OK') refresh()
  }

  return (
    <div id="content">
      <div className="row">
        <div className="col-md-12">
          <h3 style={{ marginTop: 0 }}>
            Group rules <small className="text-muted">&mdash; {data.provider.name}</small>
          </h3>
          <p className="text-muted">
            Somebody in more than one group gets the role of the <strong>first rule that matches</strong>, reading from
            the top. Drag is not needed &mdash; the order column decides.
            {data.provider.iamExtraRoles ? ' The roles of the other matching rules are added as additional roles.' : ''}
            {data.provider.iamCompanies ? ' Companies come from all the matching rules.' : ''}
          </p>
        </div>
      </div>

      {data.provider.groupsClaim === '' ? (
        <div className="note note-warning">
          This provider has no groups claim set, so these rules are never consulted. Everyone gets the default role.
        </div>
      ) : null}

      <div className="portlet light bordered">
        <div className="portlet-body">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0, flex: 1 }}>Rules</h4>
            {data.canWrite ? (
              <button
                type="button"
                className="btn btn-info btn-sm"
                onClick={() => {
                  setEditing({ ...EMPTY })
                  setOpen(true)
                }}
              >
                <i className="fas fa-plus" aria-hidden="true"></i> Add a rule
              </button>
            ) : null}
          </div>

          {data.rules.length === 0 ? (
            <div className="text-center" style={{ padding: '26px 0', color: '#999' }}>
              <i className="fas fa-users" style={{ fontSize: 26, display: 'block', marginBottom: 8 }}></i>
              No rule yet.{' '}
              {data.provider.groupsStrict
                ? 'With strict matching on, nobody can sign in through this provider.'
                : 'Everyone gets the default role.'}
            </div>
          ) : (
            <table className="table table-hover" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Order</th>
                  <th>Group</th>
                  <th>Role</th>
                  <th>Companies</th>
                  {data.ticketGroups !== undefined ? <th>Ticket groups</th> : null}
                  <th>Active</th>
                  {data.canWrite ? <th style={{ width: 110 }}></th> : null}
                </tr>
              </thead>
              <tbody>
                {data.rules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="text-muted">{rule.order}</td>
                    <td>
                      <code>{rule.group}</code>
                    </td>
                    <td>{rule.roleName || '—'}</td>
                    <td className="text-muted" style={{ fontSize: 12 }}>
                      {companiesText(rule)}
                    </td>
                    {data.ticketGroups !== undefined ? (
                      <td className="text-muted" style={{ fontSize: 12 }}>
                        {ticketGroupsText(rule)}
                      </td>
                    ) : null}
                    <td>
                      <span
                        className={`label label-${rule.status ? 'success' : 'default'}`}
                        style={{ fontWeight: 'normal' }}
                      >
                        {rule.status ? 'yes' : 'no'}
                      </span>
                    </td>
                    {data.canWrite ? (
                      <td className="text-right">
                        <button
                          type="button"
                          className="btn btn-link btn-xs"
                          onClick={() => {
                            setEditing({
                              ID: rule.id,
                              group_value: rule.group,
                              role_id: rule.roleId,
                              sort_order: rule.order,
                              status: rule.status,
                              all_companies: rule.allCompanies ?? false,
                              company_ids: rule.companyIds ?? [],
                              ticket_group_ids: rule.ticketGroupIds ?? [],
                            })
                            setOpen(true)
                          }}
                        >
                          edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-xs text-danger"
                          onClick={() => void drop(rule)}
                        >
                          remove
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 14 }}>
            <a href="/app/settings/authentication">&larr; Back to providers</a>
          </div>
        </div>
      </div>

      {data.canWrite ? <IamRulesTools page={data} providerId={providerId} onSaved={refresh} /> : null}

      {data.canWrite ? (
        <RuleModal
          page={data}
          providerId={providerId}
          form={editing}
          open={open}
          onClose={() => setOpen(false)}
          onSaved={refresh}
        />
      ) : null}
    </div>
  )
}

/**
 * "Create group rules from our roles" si importul din CSV: amandoua dau o
 * lista NESCRISA, pe care omul o vede, o schimba (grupul, ordinea, daca o ia)
 * si abia apoi o salveaza. "Pana vine lista lor, presupune ca sunt aceleasi ca
 * la noi" - iar cand vine lista bancii, se importa peste.
 */
export function IamRulesTools({
  page,
  providerId,
  onSaved,
}: {
  page: AuthGroupsPage
  providerId: number
  onSaved(): void
}) {
  const [prefix, setPrefix] = useState(page.provider.iamRulePrefix ?? '')
  const [csv, setCsv] = useState('')
  const [rows, setRows] = useState<Array<ProposedRule & { take: boolean }>>([])
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const roleName = (id: number) => page.roles.find((role) => role.id === id)?.name ?? (id > 0 ? `#${id}` : '—')

  const load = async (work: Promise<{ status: string; message: string; rows?: ProposedRule[] }>) => {
    setBusy(true)
    try {
      const answer = await work
      setNote(answer.message)
      setRows((answer.rows ?? []).map((row) => ({ ...row, take: row.error === '' })))
    } catch (error) {
      setNote(error instanceof ApiError ? error.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const readFile = (file: File | undefined) => {
    if (!file) return
    void file.text().then((text) => setCsv(text))
  }

  const save = async () => {
    const chosen = rows.filter((row) => row.take && row.error === '')
    if (chosen.length === 0) return
    setBusy(true)
    try {
      const answer = await importGroupRules(providerId, chosen)
      toast(
        answer.message,
        answer.status === 'OK' ? 'Saved' : 'Saved with problems',
        answer.status === 'OK' ? 'success' : 'error',
      )
      setNote(answer.message)
      if (answer.status === 'OK') setRows([])
      onSaved()
    } catch (error) {
      setNote(error instanceof ApiError ? error.message : 'The rules could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const edit = (index: number, patch: Partial<ProposedRule & { take: boolean }>) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  return (
    <div className="portlet light bordered">
      <div className="portlet-body">
        <h4 style={{ marginTop: 0 }}>Rules from your directory</h4>
        <p className="text-muted" style={{ fontSize: 12 }}>
          Until the customer sends the list of its groups, start from our own roles: one group per role, with the
          companies the people in that role have today. Nothing is saved until you press Save.
        </p>

        <div className="form-inline" style={{ marginBottom: 10 }}>
          <input
            type="text"
            className="form-control input-sm"
            placeholder="OD-"
            aria-label="Group prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            style={{ width: 120, marginRight: 8 }}
          />
          <button
            type="button"
            className="btn btn-default btn-sm"
            disabled={busy}
            onClick={() => void load(proposeGroupRules(providerId, prefix))}
          >
            Create group rules from our roles
          </button>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={{ fontWeight: 'normal', fontSize: 12 }} htmlFor="iamCsv">
            Or import the customer&apos;s list &mdash; one line per group: <code>group_value;role;companies;order</code>{' '}
            (role by name or number; companies separated by <code>|</code>, <code>*</code> for all, empty for none)
          </label>
          <textarea
            id="iamCsv"
            className="form-control"
            rows={4}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={'OD-Admins;Administrator;*;10\nOD-Helpdesk;Helpdesk;Company A|Company B;20'}
          />
          <div style={{ marginTop: 6 }}>
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              aria-label="CSV file"
              onChange={(e) => readFile(e.target.files?.[0])}
              style={{ display: 'inline-block', marginRight: 8 }}
            />
            <button
              type="button"
              className="btn btn-default btn-sm"
              disabled={busy || csv.trim() === ''}
              onClick={() => void load(previewGroupRulesCsv(providerId, csv))}
            >
              Preview import
            </button>
          </div>
        </div>

        {note !== '' ? (
          <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
            {note}
          </div>
        ) : null}

        {rows.length > 0 ? (
          <>
            <table className="table table-condensed" data-testid="proposed-rules">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Take</th>
                  <th>Group</th>
                  <th>Role</th>
                  <th>Companies</th>
                  <th style={{ width: 80 }}>Order</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${index}-${row.line ?? 0}`} className={row.error !== '' ? 'danger' : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Take ${row.group_value}`}
                        checked={row.take}
                        disabled={row.error !== ''}
                        onChange={(e) => edit(index, { take: e.target.checked })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control input-sm"
                        aria-label={`Group for ${roleName(row.role_id)}`}
                        value={row.group_value}
                        onChange={(e) => edit(index, { group_value: e.target.value })}
                      />
                    </td>
                    <td>{roleName(row.role_id)}</td>
                    <td className="text-muted" style={{ fontSize: 12 }}>
                      {companiesText({ allCompanies: row.all_companies, companyNames: row.company_names })}
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        className="form-control input-sm"
                        aria-label={`Order of ${row.group_value}`}
                        value={row.sort_order}
                        onChange={(e) => edit(index, { sort_order: Number(e.target.value || 0) })}
                      />
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {row.error !== '' ? (
                        <span className="text-danger">{row.error}</span>
                      ) : row.exists ? (
                        <span className="text-warning">replaces the existing rule</span>
                      ) : (
                        <span className="text-muted">new</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="btn btn-info btn-sm"
              disabled={busy || !rows.some((row) => row.take && row.error === '')}
              onClick={() => void save()}
            >
              Save the selected rules
            </button>{' '}
            <button type="button" className="btn btn-link btn-sm" onClick={() => setRows([])}>
              Discard
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}
